import React, { useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import { notify } from "../components-ui/toast";

interface ServiceDto {
  serviceId: number;
  name: string;
}

interface ServiceInstanceDto {
  serviceInstanceId: number;
  startDateTime: string;
}

interface Props {
  value: {
    serviceId: number | null;
    serviceInstanceId: number | null;
  };
  onChange: (value: {
    serviceId: number | null;
    serviceInstanceId: number | null;
    serviceName: string | null;
    sessionDateTime: string | null;
  }) => void;
  showSession?: boolean;
}

const ServiceInstanceSelector: React.FC<Props> = ({
  value,
  onChange,
  showSession = true,
}) => {
  const [services, setServices] = useState<ServiceDto[]>([]);
  const [instances, setInstances] = useState<ServiceInstanceDto[]>([]);

  /* Load services */
  useEffect(() => {
    api<ServiceDto[]>("/api/services")
      .then(setServices)
      .catch(() => notify.error("Failed to load services"));
  }, []);

  /* Load instances when service changes */
  useEffect(() => {
    if (!value.serviceId) {
      setInstances([]);
      return;
    }

    api<ServiceInstanceDto[]>(
      `/api/serviceinstances/search-by-service?serviceId=${value.serviceId}`,
    )
      .then(setInstances)
      .catch(() => notify.error("Failed to load sessions"));
  }, [value.serviceId]);

  return (
    <div className="form-row">
      {/* SERVICE */}
      <div className="form-group">
        <label className="form-label">Service</label>

        <select
          className="form-input"
          value={value.serviceId ?? ""}
          onChange={(e) => {
            const serviceId = e.target.value ? Number(e.target.value) : null;
            const serviceName =
              services.find((s) => s.serviceId === serviceId)?.name ?? null;

            onChange({
              serviceId,
              serviceInstanceId: null,
              serviceName,
              sessionDateTime: null,
            });
          }}
        >
          <option value="">Select service</option>

          {services.map((s) => (
            <option key={s.serviceId} value={s.serviceId}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {/* INSTANCE */}
      {showSession && <div className="form-group">
        <label className="form-label">Session</label>

        <select
          className="form-input"
          disabled={!value.serviceId}
          value={value.serviceInstanceId ?? ""}
          onChange={(e) => {
            const instanceId = e.target.value ? Number(e.target.value) : null;
            const sessionDateTime =
              instances.find((si) => si.serviceInstanceId === instanceId)
                ?.startDateTime ?? null;
            const serviceName =
              services.find((s) => s.serviceId === value.serviceId)?.name ??
              null;

            onChange({
              ...value,
              serviceInstanceId: instanceId,
              serviceName,
              sessionDateTime,
            });
          }}
        >
          <option value="">
            {value.serviceId ? "Select a session" : "Select a service first"}
          </option>

          {instances.map((si) => (
            <option key={si.serviceInstanceId} value={si.serviceInstanceId}>
              {new Date(si.startDateTime).toLocaleString()}
            </option>
          ))}
        </select>
      </div>}
    </div>
  );
};

export default ServiceInstanceSelector;
